import { useState, useMemo } from "react";
import { CircleLoader } from "../../components/CircleLoader";
import {
  ArrowRightLeft,
  Search,
  UserPlus,
  UserMinus,
  CheckCircle2,
  X,
  Info,
  Users,
  AlertCircle,
} from "lucide-react";

export const TransferForm = ({
  form,
  handleChange,
  handleSubmit,
  classrooms,
  isLoading,
  studentData,
  classId,
}: any) => {
  const [search, setSearch] = useState("");

  // ================= LOGIC: DATA HANDLING =================
  const displayList = useMemo(() => {
    const list =
      studentData?.filter((student: any) => {
        if (!classId) return true;
        return student.classroom_id?.toString() === classId?.toString();
      }) || [];

    return list.length > 0 ? list : studentData || [];
  }, [studentData, classId]);

  const searchedStudents = displayList.filter((s: any) =>
    `${s.firstname} ${s.surname}`.toLowerCase().includes(search.toLowerCase()),
  );

  const categories = [
    { value: "academic_performance", label: "Academic Performance" },
    { value: "bullying_harassment", label: "Bullying / Harassment" },
    { value: "teacher_compatibility", label: "Teacher Compatibility" },
    { value: "behavioral_issues", label: "Behavioral Issues" },
    { value: "special_needs_support", label: "Special Needs Support" },
    { value: "social_integration", label: "Social Integration" },
    { value: "class_size_adjustment", label: "Class Size Adjustment" },
    { value: "other", label: "Others" },
  ];

  // ================= LOGIC: TOGGLE ALL (FIXED) =================
  const isAllSelected =
    searchedStudents.length > 0 &&
    searchedStudents.every((s: any) => form.student_ids.includes(s.id));

  const handleToggleAll = () => {
    if (isAllSelected) {
      // Remove visible students from the selection
      const searchedIds = searchedStudents.map((s: any) => s.id);
      const updated = form.student_ids.filter(
        (id: string) => !searchedIds.includes(id),
      );
      handleChange({ target: { name: "student_ids", value: updated } });
    } else {
      // Add visible students to selection (using a Set to ensure uniqueness)
      const newIds = Array.from(
        new Set([
          ...form.student_ids,
          ...searchedStudents.map((s: any) => s.id),
        ]),
      );
      // CRITICAL FIX: Actually calling handleChange here
      handleChange({ target: { name: "student_ids", value: newIds } });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-8 pb-10">
      {/* CARD 1: DESTINATION */}
      <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm space-y-4 transition-all hover:shadow-md">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-2xl">
            <ArrowRightLeft size={20} />
          </div>
          <div>
            <h3 className="font-bold text-slate-800 tracking-tight">
              Destination
            </h3>
            <p className="text-xs text-slate-400 font-medium">
              Where are these pupils moving to?
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <select
            name="to_classroom_id"
            value={form.to_classroom_id}
            onChange={handleChange}
            className="w-full h-14 px-5 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-all appearance-none font-semibold text-slate-700"
            required
          >
            <option value="">Select Target Class</option>
            {classrooms?.map((cls: any) => (
              <option key={cls.id} value={cls.id}>
                {cls.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* CARD 2: STUDENT SELECTION */}
      <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm space-y-5 transition-all hover:shadow-md">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-50 text-blue-600 rounded-2xl">
              <Users size={20} />
            </div>
            <div>
              <h3 className="font-bold text-slate-800 tracking-tight">
                Select Pupils
              </h3>
              <p className="text-xs text-slate-400 font-medium">
                {displayList.length} total available
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleToggleAll}
            className={`text-xs font-black px-4 py-2.5 rounded-xl transition-all uppercase tracking-tighter shadow-sm border ${
              isAllSelected
                ? "bg-slate-800 text-white border-slate-800 hover:bg-slate-900"
                : "bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-600 hover:text-white"
            }`}
          >
            {isAllSelected ? "Deselect All" : "Select All Visible"}
          </button>
        </div>

        {/* Selected Chips Area */}
        {form.student_ids.length > 0 && (
          <div className="flex flex-wrap gap-2 p-4 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
            {form.student_ids?.map((id: string) => {
              const student = studentData?.find((s: any) => s.id === id);
              return (
                <div
                  key={id}
                  className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl text-[11px] font-bold shadow-sm animate-in fade-in zoom-in duration-300"
                >
                  <span className="opacity-60">{student?.surname}</span>{" "}
                  {student?.firstname}
                  <X
                    size={14}
                    className="cursor-pointer text-slate-300 hover:text-red-500 transition-colors"
                    onClick={() => {
                      const updated = form.student_ids.filter(
                        (sid: string) => sid !== id,
                      );
                      handleChange({
                        target: { name: "student_ids", value: updated },
                      });
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Search Input */}
        <div className="relative group">
          <Search
            className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-emerald-500 transition-colors"
            size={18}
          />
          <input
            type="text"
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-12 pl-12 pr-5 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-all font-medium"
          />
        </div>

        {/* The List Container */}
        <div className="max-h-[320px] overflow-y-auto pr-2 space-y-2 custom-scrollbar">
          {searchedStudents.length > 0 ? (
            searchedStudents.map((student: any) => {
              const isSelected = form.student_ids.includes(student.id);
              return (
                <div
                  key={student.id}
                  onClick={() => {
                    const updated = isSelected
                      ? form.student_ids.filter(
                          (id: string) => id !== student.id,
                        )
                      : [...form.student_ids, student.id];
                    handleChange({
                      target: { name: "student_ids", value: updated },
                    });
                  }}
                  className={`flex items-center justify-between p-4 rounded-2xl cursor-pointer border transition-all duration-300 group/item ${
                    isSelected
                      ? "bg-emerald-50 border-emerald-300 shadow-sm scale-[1.01]"
                      : "bg-white border-slate-50 hover:border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-xs transition-all duration-300 ${isSelected ? "bg-emerald-500 text-white rotate-6" : "bg-slate-100 text-slate-400"}`}
                    >
                      {student.firstname[0]}
                      {student.surname[0]}
                    </div>
                    <div>
                      <p
                        className={`text-sm font-bold transition-colors ${isSelected ? "text-emerald-900" : "text-slate-700"}`}
                      >
                        {student.surname} {student.firstname}
                      </p>
                      <p className="text-[10px] text-slate-400 font-medium uppercase tracking-tighter">
                        Current Pupil
                      </p>
                    </div>
                  </div>
                  {isSelected ? (
                    <div className="p-1.5 bg-emerald-500 text-white rounded-lg shadow-md animate-in zoom-in duration-200">
                      <CheckCircle2 size={16} />
                    </div>
                  ) : (
                    <UserPlus
                      size={18}
                      className="text-slate-300 group-hover/item:text-slate-500 transition-colors"
                    />
                  )}
                </div>
              );
            })
          ) : (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center bg-slate-50 rounded-[2rem] border border-dashed border-slate-200">
              <AlertCircle size={32} className="text-slate-300 mb-3" />
              <p className="text-sm font-bold text-slate-500">
                No Pupils Found
              </p>
              <p className="text-xs text-slate-400">
                Try adjusting your search filter.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* CARD 3: JUSTIFICATION */}
      <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm space-y-6 transition-all hover:shadow-md">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-amber-50 text-amber-600 rounded-2xl">
            <Info size={20} />
          </div>
          <div>
            <h3 className="font-bold text-slate-800 tracking-tight">
              Justification
            </h3>
            <p className="text-xs text-slate-400 font-medium">
              Why is this transfer necessary?
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <select
            name="category"
            value={form.category}
            onChange={handleChange}
            className="w-full h-14 px-5 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-all font-semibold text-slate-700"
            required
          >
            <option value="">Select a category</option>
            {categories.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>

          <textarea
            name="reason_description"
            value={form.reason_description}
            onChange={handleChange}
            placeholder="Provide specific notes regarding the transfer..."
            className="w-full p-5 bg-slate-50 border border-slate-200 rounded-[1.5rem] min-h-[140px] focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-all text-slate-700 font-medium placeholder:text-slate-300"
            required
          />
        </div>
      </div>

      {/* ACTION BUTTON */}
      <button
        type="submit"
        disabled={isLoading || form.student_ids.length === 0}
        className={`group relative w-full h-16 rounded-2xl font-black text-lg transition-all duration-500 overflow-hidden shadow-xl
          ${
            form.student_ids.length > 0
              ? "bg-slate-900 text-white hover:bg-black hover:-translate-y-1 active:scale-[0.98] shadow-emerald-900/10"
              : "bg-slate-100 text-slate-300 cursor-not-allowed shadow-none"
          }
        `}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

        {isLoading ? (
          <div className="flex items-center justify-center gap-3 relative z-10">
            <CircleLoader />
            <span className="uppercase tracking-widest text-sm">
              Processing...
            </span>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-3 relative z-10">
            <CheckCircle2
              size={22}
              className={form.student_ids.length > 0 ? "text-emerald-400" : ""}
            />
            <span className="uppercase tracking-widest text-sm">
              Confirm Transfer{" "}
              {form.student_ids.length > 0 && `(${form.student_ids.length})`}
            </span>
          </div>
        )}
      </button>
    </form>
  );
};
